/**
 * FormFlu Authentication Context
 * 
 * HIPAA COMPLIANCE NOTES:
 * - User data is not PHI (email, name, role)
 * - NO PHI should ever be stored in this context
 * - Session clears on browser close (sessionStorage)
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type {
  User,
  LoginRequest,
  RegistrationRequest,
  MFASetupData,
  UserDisplay,
} from '@formflow/shared';
import {
  authApi,
  initializeAuth,
  clearTokens,
  getMfaToken,
  ApiClientError,
  type FieldErrors,
} from '../lib/api';

// =============================================================================
// Types
// =============================================================================

/** Auth flow step */
export type AuthStep =
  | 'idle'
  | 'credentials'
  | 'mfa_verify'
  | 'mfa_setup'
  | 'recovery_codes'
  | 'authenticated';

/** Auth state */
export interface AuthState {
  /** Current auth flow step */
  step: AuthStep;
  /** Current user (null if not authenticated) */
  user: User | null;
  /** MFA setup data (during setup flow) */
  mfaSetupData: MFASetupData | null;
  /** Recovery codes (shown once after MFA setup) */
  recoveryCodes: string[];
  /** Loading state */
  isLoading: boolean;
  /** Error message */
  error: string | null;
  /** Field-specific validation errors */
  fieldErrors: FieldErrors;
  /** Is user fully authenticated? */
  isAuthenticated: boolean;
}

/** Auth context value */
export interface AuthContextValue extends AuthState {
  /** Register new user and organization */
  register: (data: RegistrationRequest) => Promise<void>;
  /** Login with email/password (step 1) */
  login: (credentials: LoginRequest) => Promise<void>;
  /** Verify MFA code (step 2a - existing MFA) */
  verifyMfa: (code: string) => Promise<void>;
  /** Use recovery code (step 2b - alternative) */
  useRecoveryCode: (code: string) => Promise<void>;
  /** Setup MFA (step 2c - new users) */
  startMfaSetup: () => Promise<void>;
  /** Complete MFA setup (step 2d - verify initial code) */
  completeMfaSetup: (code: string, secret: string) => Promise<void>;
  /** Acknowledge recovery codes */
  acknowledgeRecoveryCodes: () => void;
  /** Logout */
  logout: () => Promise<void>;
  /** Clear error */
  clearError: () => void;
  /** User display info */
  userDisplay: UserDisplay | null;
}

// =============================================================================
// Context
// =============================================================================

const AuthContext = createContext<AuthContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    step: 'idle',
    user: null,
    mfaSetupData: null,
    recoveryCodes: [],
    isLoading: true,
    error: null,
    fieldErrors: {},
    isAuthenticated: false,
  });

  // Initialize auth from session storage and restore session
  useEffect(() => {
    const init = async () => {
      // Initialize tokens from session storage
      initializeAuth();
      
      // Check if we have a stored refresh token
      const storedRefresh = sessionStorage.getItem('ff_refresh');
      
      if (!storedRefresh) {
        // No refresh token, user needs to login
        setState(prev => ({
          ...prev,
          step: 'idle',
          isLoading: false,
        }));
        return;
      }
      
      // Try to refresh the access token and get user
      try {
        // First try to refresh the token
        await authApi.refresh();
        
        // Then get the current user
        const user = await authApi.getMe();
        setState(prev => ({
          ...prev,
          step: 'authenticated',
          user,
          isAuthenticated: true,
          isLoading: false,
        }));
      } catch {
        // Refresh failed, clear tokens and require login
        clearTokens();
        setState(prev => ({
          ...prev,
          step: 'idle',
          isLoading: false,
        }));
      }
    };
    
    init();
  }, []);

  // Register new user and organization
  const register = useCallback(async (data: RegistrationRequest) => {
    setState(prev => ({ ...prev, isLoading: true, error: null, fieldErrors: {} }));
    
    try {
      await authApi.register(data);
      
      // Registration always requires MFA setup for new users
      setState(prev => ({
        ...prev,
        step: 'mfa_setup',
        isLoading: false,
      }));
    } catch (err) {
      let message = 'Registration failed. Please try again.';
      let fieldErrors: FieldErrors = {};
      
      if (err instanceof ApiClientError) {
        // If there are field-specific errors, use a generic message
        // The specific errors will be shown next to each field
        if (err.hasFieldErrors()) {
          fieldErrors = err.fieldErrors;
          message = 'Please correct the errors below';
        } else {
          message = err.error.message;
        }
      }
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: message,
        fieldErrors,
      }));
    }
  }, []);

  // Login with email/password
  const login = useCallback(async (credentials: LoginRequest) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await authApi.login(credentials);
      
      if (response.mfa_setup_required) {
        // User needs to setup MFA
        setState(prev => ({
          ...prev,
          step: 'mfa_setup',
          isLoading: false,
        }));
      } else {
        // User has MFA, needs to verify
        setState(prev => ({
          ...prev,
          step: 'mfa_verify',
          isLoading: false,
        }));
      }
    } catch (err) {
      const message = err instanceof ApiClientError
        ? err.error.message
        : 'An unexpected error occurred';
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
    }
  }, []);

  // Verify MFA code
  const verifyMfa = useCallback(async (code: string) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    const mfaToken = getMfaToken();
    if (!mfaToken) {
      setState(prev => ({
        ...prev,
        step: 'idle',
        isLoading: false,
        error: 'Session expired. Please login again.',
      }));
      return;
    }
    
    try {
      const response = await authApi.verifyMfa({ mfa_token: mfaToken, code });
      
      setState(prev => ({
        ...prev,
        step: 'authenticated',
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
      }));
    } catch (err) {
      const message = err instanceof ApiClientError
        ? err.error.message
        : 'Invalid verification code';
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
    }
  }, []);

  // Use recovery code
  const useRecoveryCode = useCallback(async (code: string) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    const mfaToken = getMfaToken();
    if (!mfaToken) {
      setState(prev => ({
        ...prev,
        step: 'idle',
        isLoading: false,
        error: 'Session expired. Please login again.',
      }));
      return;
    }
    
    try {
      const response = await authApi.useRecoveryCode({
        mfa_token: mfaToken,
        recovery_code: code,
      });
      
      setState(prev => ({
        ...prev,
        step: 'authenticated',
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
      }));
    } catch (err) {
      const message = err instanceof ApiClientError
        ? err.error.message
        : 'Invalid recovery code';
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
    }
  }, []);

  // Start MFA setup
  const startMfaSetup = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const setupData = await authApi.getMfaSetup();
      
      setState(prev => ({
        ...prev,
        step: 'mfa_setup',
        mfaSetupData: setupData,
        isLoading: false,
      }));
    } catch (err) {
      const message = err instanceof ApiClientError
        ? err.error.message
        : 'Failed to initialize MFA setup';
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
    }
  }, []);

  // Complete MFA setup
  const completeMfaSetup = useCallback(async (code: string, secret: string) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await authApi.verifyMfaSetup({ code, secret });
      
      setState(prev => ({
        ...prev,
        step: 'recovery_codes',
        user: response.user,
        recoveryCodes: response.recovery_codes,
        mfaSetupData: null,
        isLoading: false,
      }));
    } catch (err) {
      const message = err instanceof ApiClientError
        ? err.error.message
        : 'Invalid verification code';
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
    }
  }, []);

  // Acknowledge recovery codes
  const acknowledgeRecoveryCodes = useCallback(() => {
    setState(prev => ({
      ...prev,
      step: 'authenticated',
      recoveryCodes: [],
      isAuthenticated: true,
    }));
  }, []);

  // Logout
  const logout = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    
    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors, still clear local state
    }
    
    clearTokens();
    
    setState({
      step: 'idle',
      user: null,
      mfaSetupData: null,
      recoveryCodes: [],
      isLoading: false,
      error: null,
      fieldErrors: {},
      isAuthenticated: false,
    });
  }, []);

  // Clear error (both general error and field errors)
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null, fieldErrors: {} }));
  }, []);

  // User display info
  const userDisplay = useMemo((): UserDisplay | null => {
    if (!state.user) return null;
    
    const firstName = state.user.first_name || '';
    const lastName = state.user.last_name || '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ') || state.user.email;
    const initials = firstName && lastName
      ? `${firstName[0]}${lastName[0]}`.toUpperCase()
      : state.user.email.slice(0, 2).toUpperCase();
    
    return {
      id: state.user.id,
      email: state.user.email,
      firstName,
      lastName,
      fullName,
      initials,
      role: state.user.role,
    };
  }, [state.user]);

  const value = useMemo((): AuthContextValue => ({
    ...state,
    register,
    login,
    verifyMfa,
    useRecoveryCode,
    startMfaSetup,
    completeMfaSetup,
    acknowledgeRecoveryCodes,
    logout,
    clearError,
    userDisplay,
  }), [
    state,
    register,
    login,
    verifyMfa,
    useRecoveryCode,
    startMfaSetup,
    completeMfaSetup,
    acknowledgeRecoveryCodes,
    logout,
    clearError,
    userDisplay,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}

/**
 * Hook to check if user is authenticated
 */
export function useIsAuthenticated(): boolean {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
}

/**
 * Hook to get current user
 */
export function useUser(): User | null {
  const { user } = useAuth();
  return user;
}

/**
 * Hook to get user display info
 */
export function useUserDisplay(): UserDisplay | null {
  const { userDisplay } = useAuth();
  return userDisplay;
}
